/**
 * Global RealtimeProvider — one EventSource for the entire dashboard.
 *
 * Usage:
 *   const { status, subscribe } = useRealtime();
 *   useEffect(() => subscribe("order.created", (evt) => {...}), []);
 *
 * Convenience hook:
 *   useRealtimeEvent("order.created", handler, [deps]);
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { RealtimeClient } from "./realtime";
import { useAuth } from "./auth";

const RealtimeCtx = createContext({
  status: "offline",
  subscribe: () => () => {},
  lastEvent: null,
});

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState("offline");
  const [lastEvent, setLastEvent] = useState(null);
  const clientRef = useRef(null);

  // Always-on global toast handlers (orders + returns).
  // Stored as a ref so route components can also subscribe their own callbacks.
  const handleGlobalOrderCreated = useCallback((evt) => {
    const num = evt?.data?.order_number || evt?.data?.name || evt?.data?.id;
    toast.success(`Comandă nouă${num ? ` · #${num}` : ""}`, {
      description: evt?.data?.customer_name || evt?.data?.email || undefined,
      duration: 6000,
    });
  }, []);

  const handleGlobalReturnCreated = useCallback((evt) => {
    const num = evt?.data?.return_number || evt?.data?.id;
    toast(`Retur nou${num ? ` · ${num}` : ""}`, {
      description: evt?.data?.customer_name || evt?.data?.order_number || undefined,
      duration: 6000,
    });
  }, []);

  useEffect(() => {
    if (!user) {
      // No user → ensure any old client is closed and status is offline.
      if (clientRef.current) { clientRef.current.close(); clientRef.current = null; }
      setStatus("offline");
      return;
    }

    const c = new RealtimeClient({
      onStatus: (s) => setStatus(s),
    });
    clientRef.current = c;

    // Wire global handlers BEFORE connecting so they're attached on first open.
    c.on("order.created", (evt) => { setLastEvent(evt); handleGlobalOrderCreated(evt); });
    c.on("order.updated", (evt) => setLastEvent(evt));
    c.on("order.cancelled", (evt) => setLastEvent(evt));
    c.on("fulfillment.created", (evt) => setLastEvent(evt));
    c.on("fulfillment.updated", (evt) => setLastEvent(evt));
    c.on("refund.created", (evt) => setLastEvent(evt));
    c.on("return.created", (evt) => { setLastEvent(evt); handleGlobalReturnCreated(evt); });
    c.on("return.updated", (evt) => setLastEvent(evt));
    c.on("service.created", (evt) => setLastEvent(evt));
    c.on("service.updated", (evt) => setLastEvent(evt));
    c.on("system.ping", (evt) => setLastEvent(evt));

    c.connect();
    return () => { c.close(); clientRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const subscribe = useCallback((topic, fn) => {
    if (!clientRef.current) return () => {};
    return clientRef.current.on(topic, fn);
  }, []);

  const value = useMemo(() => ({ status, subscribe, lastEvent }), [status, subscribe, lastEvent]);
  return <RealtimeCtx.Provider value={value}>{children}</RealtimeCtx.Provider>;
}

export function useRealtime() {
  return useContext(RealtimeCtx);
}

/**
 * Convenience hook. Subscribes to a single topic and auto-unsubscribes on unmount.
 *   useRealtimeEvent("order.created", (evt) => reload(), [reload]);
 */
export function useRealtimeEvent(topic, handler, deps = []) {
  const { subscribe } = useRealtime();
  useEffect(() => {
    const off = subscribe(topic, handler);
    return off;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, topic, ...deps]);
}
